import { Database, Workflow, KeyRound, Cpu, ShieldOff, FileSearch, Globe2, ArrowRight } from "lucide-react";

const TEAL = "#00586B";
const GOLD = "#FFC425";
const CHARCOAL = "#1A1A1A";

const STEPS = [
  { n: "1", t: "Originates in your tenant", b: "A request starts inside your AWS environment — source data is read from your S3 / RDS / CMS using scoped IAM roles." },
  { n: "2", t: "Encrypted with your KMS key", b: "Before the prompt leaves your VPC it's encrypted with a customer-managed key. You control the key policy and can revoke access at any time." },
  { n: "3", t: "PrivateLink to Bedrock — inference, then back", b: "The request never touches the public internet. Claude runs in an AWS-operated environment segregated from Anthropic; the completion returns to your VPC." },
  { n: "4", t: "Audit trail written to your CloudTrail", b: "Every invocation logs who called, what model, when, from which role — generated in, and owned by, your AWS account." },
];

const CONTROLS = [
  { Icon: KeyRound, t: "Customer-managed KMS keys", b: "A key policy that names exactly which roles may encrypt or decrypt — auditable in your own console." },
  { Icon: FileSearch, t: "Audit logs you own", b: "CloudTrail data events for every model call live in your account — not a vendor's logging system." },
  { Icon: Globe2, t: "Region pinning + least privilege", b: "Data residency enforced by AWS per region; scoped IAM roles, no long-lived credentials, no shared accounts." },
];

function VpcBox({ Icon, t, b }) {
  return (
    <div className="rounded-lg p-3.5 flex items-start gap-3" style={{ background: "#fff", border: "1px solid rgba(26,26,26,0.12)" }}>
      <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} strokeWidth={1.8} />
      <div>
        <div className="text-[13px] font-bold">{t}</div>
        <div className="text-[11.5px] leading-snug mt-0.5" style={{ opacity: 0.65 }}>{b}</div>
      </div>
    </div>
  );
}

export function FlagstarDataPrivacy() {
  return (
    <section id="privacy" className="fs-anchor py-14 md:py-20" data-testid="fs-privacy-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="fs-eyebrow">Data privacy · how we protect your data</div>
        <h2 className="fs-h text-4xl md:text-5xl mt-3 max-w-3xl">
          Your data never leaves your cloud. <span style={{ color: TEAL }}>Your keys. Your logs. Your control.</span>
        </h2>
        <p className="mt-4 text-lg max-w-3xl leading-relaxed" style={{ opacity: 0.72 }}>
          The AI layer runs on AWS Bedrock <strong>inside your own AWS account</strong> — not on Claude
          Enterprise or ChatGPT Enterprise. Prompts and completions are never uploaded to Anthropic's
          systems, never used for model training, never reviewed by humans, and never retained.
        </p>

        {/* Architecture diagram */}
        <div className="mt-9 fs-card p-5 md:p-7" data-testid="fs-privacy-diagram">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
            {/* Client account */}
            <div className="rounded-xl p-4 md:p-5" style={{ background: `${TEAL}08`, border: `2px solid ${TEAL}55` }}>
              <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] mb-3" style={{ color: TEAL }}>
                Client AWS account · your VPC
              </div>
              <div className="space-y-2.5">
                <VpcBox Icon={Database} t="CompliSun CMS & data sources" b="S3, RDS, systems of record — stays in your account at rest." />
                <VpcBox Icon={Workflow} t="Workflow orchestrator" b="Agentic logic and retrieval, running in your subnet under your IAM." />
                <VpcBox Icon={KeyRound} t="CloudTrail + KMS" b="Your keys encrypt everything; every AI call is logged to your CloudTrail." />
              </div>
            </div>

            {/* PrivateLink connector */}
            <div className="flex lg:flex-col items-center justify-center gap-2 py-1">
              <div className="hidden lg:block h-8 w-px" style={{ background: `${CHARCOAL}25` }} />
              <div className="text-center px-3 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap"
                   style={{ background: GOLD, color: CHARCOAL }}>
                AWS PrivateLink
              </div>
              <ArrowRight className="h-5 w-5 rotate-90 lg:rotate-0" style={{ color: TEAL }} />
              <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-center max-w-[120px]" style={{ opacity: 0.55 }}>
                AWS network only
              </div>
              <div className="hidden lg:block h-8 w-px" style={{ background: `${CHARCOAL}25` }} />
            </div>

            {/* Bedrock env */}
            <div className="rounded-xl p-4 md:p-5" style={{ background: "rgba(26,26,26,0.03)", border: "2px dashed rgba(26,26,26,0.25)" }}>
              <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] mb-3" style={{ opacity: 0.6 }}>
                AWS Bedrock service environment · AWS-managed
              </div>
              <div className="rounded-lg p-4" style={{ background: "#fff", border: "1px solid rgba(26,26,26,0.12)" }}>
                <div className="flex items-center gap-2.5 mb-2">
                  <Cpu className="h-4 w-4" style={{ color: TEAL }} strokeWidth={1.8} />
                  <span className="text-[13px] font-bold">Claude on Bedrock</span>
                </div>
                <ul className="grid grid-cols-2 gap-1.5 text-[11.5px]" style={{ opacity: 0.72 }}>
                  {["Stateless inference", "No retention", "No training use", "No human review"].map((x) => (
                    <li key={x} className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: TEAL }} />{x}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-2.5 rounded-lg p-3.5 flex items-start gap-2.5" style={{ background: "#fff", border: "1px solid rgba(26,26,26,0.12)" }}>
                <ShieldOff className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ opacity: 0.5 }} strokeWidth={1.8} />
                <div className="text-[11.5px] leading-snug" style={{ opacity: 0.65 }}>
                  <strong>Anthropic — model weights provider only.</strong> Not provisioned to access your
                  deployment account, prompts, or completions.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 text-center py-2 rounded-lg text-[11px] font-extrabold uppercase tracking-[0.2em]"
               style={{ background: `${CHARCOAL}06`, color: CHARCOAL, border: "1px solid rgba(26,26,26,0.1)", opacity: 0.85 }}>
            No public internet path — verifiable in your VPC route table
          </div>
        </div>

        {/* What happens to a single prompt */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map(({ n, t, b }) => (
            <div key={n} className="fs-card p-5 relative" data-testid={`fs-privacy-step-${n}`}>
              <div className="absolute top-3 right-4 fs-h text-3xl" style={{ color: `${TEAL}18` }}>{n}</div>
              <div className="fs-h text-[15px] mb-1.5 pr-6">{t}</div>
              <p className="text-[12.5px] leading-relaxed" style={{ opacity: 0.7 }}>{b}</p>
            </div>
          ))}
        </div>

        {/* Verifiable controls */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {CONTROLS.map(({ Icon, t, b }) => (
            <div key={t} className="fs-card p-5 flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg grid place-items-center flex-shrink-0" style={{ background: `${TEAL}0f` }}>
                <Icon className="h-4.5 w-4.5" style={{ color: TEAL, height: 18, width: 18 }} strokeWidth={1.8} />
              </div>
              <div>
                <div className="fs-h text-[15px]">{t}</div>
                <p className="text-[12.5px] leading-relaxed mt-1" style={{ opacity: 0.7 }}>{b}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 fs-card p-5 md:p-6 flex items-center gap-5 max-w-3xl" data-testid="fs-soc2-badge">
          <img src="/brand/soc2-badge.png" alt="SOC 2 Type II compliant" className="h-24 w-auto flex-shrink-0" />
          <div>
            <div className="fs-h text-lg">SOC 2 Type II compliant</div>
            <p className="text-[13px] leading-relaxed mt-1" style={{ opacity: 0.7 }}>
              CompliSun's own controls are independently audited over time — not just designed on paper.
              Security, availability, and confidentiality, verified by a third-party auditor. Report
              available under NDA.
            </p>
          </div>
        </div>

        <p className="mt-5 text-[12px] max-w-3xl" style={{ opacity: 0.55 }}>
          Every control above is independently testable in your own AWS console — key policies via IAM
          Access Analyzer, invocation logs in CloudTrail, and the network path in your VPC route tables.
          Full architecture documentation available on request.
        </p>
      </div>
    </section>
  );
}
